import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, TextInput, View, ScrollView, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InlineSelect } from '@/components/profile/whatsapp-template-field-select';
import { FEATURES } from '@/constants/feature-flags';
import api from '@/services/api';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';

type WhatsappVariableBinding = {
  table: string;
  column: string;
  via?: string;
};

type WhatsappTemplate = {
  id: string;
  code: string;
  templateName: string;
  description?: string | null;
  languageCode: string;
  templateText?: string | null;
  variablesExample?: Record<string, WhatsappVariableBinding | string> | null;
  hasButtonUrl: boolean;
  buttonUrlParamDescription?: string | null;
};

type SchemaColumn = { name: string; dataType: string; label: string };
type SchemaTable = {
  name: string;
  label: string;
  columns: SchemaColumn[];
  requiresVia?: boolean;
  viaOptions?: { value: string; label: string }[];
};

type BindingRow = { key: string; binding: WhatsappVariableBinding };

const FIXED_TEMPLATE_CODES = [
  'charge_default',
  'payment_received_default',
  'password_setup_default',
  'test_default',
] as const;

const FIXED_TEMPLATE_LABELS: Record<(typeof FIXED_TEMPLATE_CODES)[number], string> = {
  charge_default: 'Lembrete de cobrança',
  payment_received_default: 'Pagamento recebido',
  password_setup_default: 'Setup de senha',
  test_default: 'Mensagem de teste',
};

function isFixedCode(code: string): boolean {
  return (FIXED_TEMPLATE_CODES as readonly string[]).includes(code);
}

function isBinding(value: unknown): value is WhatsappVariableBinding {
  return typeof value === 'object' && value !== null && 'table' in value && 'column' in value;
}

function extractPlaceholderKeys(templateText: string): string[] {
  const keys = new Set<string>();
  templateText.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    keys.add(key);
    return '';
  });
  return Array.from(keys).sort((a, b) => Number(a) - Number(b));
}

function bindingLabel(binding: WhatsappVariableBinding, schemaTables: SchemaTable[]): string {
  const table = schemaTables.find((t) => t.name === binding.table);
  const column = table?.columns.find((c) => c.name === binding.column);
  const via = binding.via
    ? (table?.viaOptions?.find((v) => v.value === binding.via)?.label ?? binding.via)
    : null;
  const col = column?.label ?? binding.column;
  const tbl = table?.label ?? binding.table;
  return via ? `${tbl} (${via}) → ${col}` : `${tbl} → ${col}`;
}

function buildPreviewFromBindings(
  templateText: string,
  bindings: Record<string, WhatsappVariableBinding>,
  schemaTables: SchemaTable[],
): string {
  return templateText.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    const b = bindings[key];
    if (!b?.table || !b?.column) return `{{${key}}}`;
    return `[${bindingLabel(b, schemaTables)}]`;
  });
}

export function WhatsappTemplatesManager() {
  const { addToast } = useToastStore();
  const isEvolutionMode = FEATURES.WHATSAPP_PROVIDER === 'evolution';

  const [loading, setLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [schemaTables, setSchemaTables] = useState<SchemaTable[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const [code, setCode] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateText, setTemplateText] = useState('');
  const [description, setDescription] = useState('');
  const [languageCode, setLanguageCode] = useState('pt_BR');
  const [hasButtonUrl, setHasButtonUrl] = useState(false);
  const [buttonUrlParamDescription, setButtonUrlParamDescription] = useState('');
  const [bindingRows, setBindingRows] = useState<BindingRow[]>([]);

  const isEditing = !!selectedTemplateId;

  const displayTemplates = useMemo(() => {
    const list = isEvolutionMode ? templates.filter((t) => isFixedCode(t.code)) : templates;
    const order = FIXED_TEMPLATE_CODES as readonly string[];
    return [...list].sort((a, b) => {
      const ai = order.indexOf(a.code);
      const bi = order.indexOf(b.code);
      if (ai === -1 && bi === -1) return a.code.localeCompare(b.code);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [templates, isEvolutionMode]);

  const bindingsMap = useMemo(() => {
    const map: Record<string, WhatsappVariableBinding> = {};
    for (const row of bindingRows) {
      if (row.key.trim() && row.binding.table && row.binding.column) {
        map[row.key.trim()] = {
          table: row.binding.table,
          column: row.binding.column,
          via: row.binding.via || undefined,
        };
      }
    }
    return map;
  }, [bindingRows]);

  const placeholderKeys = useMemo(() => extractPlaceholderKeys(templateText), [templateText]);

  const missingBindings = useMemo(
    () => placeholderKeys.filter((key) => !bindingsMap[key]?.column),
    [placeholderKeys, bindingsMap],
  );

  const previewText = useMemo(() => {
    if (!templateText.trim()) return '';
    return buildPreviewFromBindings(templateText, bindingsMap, schemaTables);
  }, [templateText, bindingsMap, schemaTables]);

  const loadSchema = useCallback(async () => {
    setSchemaLoading(true);
    try {
      const res = await api.get<{ tables: SchemaTable[] }>('/whatsapp-templates/schema');
      setSchemaTables(res.data?.tables ?? []);
    } catch (error) {
      console.error('Erro ao carregar schema', error);
      addToast('Não foi possível carregar tabelas do sistema.', 'error');
    } finally {
      setSchemaLoading(false);
    }
  }, [addToast]);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<WhatsappTemplate[]>('/whatsapp-templates');
      setTemplates(res.data || []);
    } catch (error) {
      console.error('Erro ao carregar templates de WhatsApp', error);
      addToast('Não foi possível carregar os modelos de WhatsApp.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadTemplates();
    if (isEvolutionMode) loadSchema();
  }, [loadTemplates, loadSchema, isEvolutionMode]);

  useEffect(() => {
    if (!isEvolutionMode || selectedTemplateId || displayTemplates.length === 0) return;
    handleSelectTemplate(displayTemplates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayTemplates, isEvolutionMode, selectedTemplateId]);

  useEffect(() => {
    if (!isEvolutionMode || !templateText) return;
    setBindingRows((prev) => {
      const existing = new Map(prev.map((r) => [r.key, r]));
      return placeholderKeys.map((key) => ({
        key,
        binding: existing.get(key)?.binding ?? { table: '', column: '' },
      }));
    });
  }, [placeholderKeys.join(','), isEvolutionMode]);

  const resetForm = () => {
    setSelectedTemplateId(null);
    setCode('');
    setTemplateName('');
    setTemplateText('');
    setDescription('');
    setLanguageCode('pt_BR');
    setHasButtonUrl(false);
    setButtonUrlParamDescription('');
    setBindingRows([]);
  };

  const handleSelectTemplate = (tpl: WhatsappTemplate) => {
    setSelectedTemplateId(tpl.id);
    setCode(tpl.code);
    setTemplateName(tpl.templateName);
    setTemplateText(tpl.templateText || '');
    setDescription(tpl.description || '');
    setLanguageCode(tpl.languageCode || 'pt_BR');
    setHasButtonUrl(!!tpl.hasButtonUrl);
    setButtonUrlParamDescription(tpl.buttonUrlParamDescription || '');

    const vars = tpl.variablesExample || {};
    const keysFromText = extractPlaceholderKeys(tpl.templateText || '');
    const allKeys = Array.from(new Set([...keysFromText, ...Object.keys(vars)])).sort(
      (a, b) => Number(a) - Number(b),
    );

    setBindingRows(
      allKeys.map((key) => {
        const raw = vars[key];
        const binding: WhatsappVariableBinding = isBinding(raw)
          ? { table: raw.table, column: raw.column, via: raw.via }
          : { table: '', column: '' };
        return { key, binding };
      }),
    );
  };

  const updateBindingRow = (index: number, patch: Partial<WhatsappVariableBinding>) => {
    setBindingRows((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        binding: { ...copy[index].binding, ...patch },
      };
      if (patch.table && patch.table !== copy[index].binding.table) {
        copy[index].binding.column = '';
        copy[index].binding.via = undefined;
      }
      return copy;
    });
  };

  const handleSave = async () => {
    if (isEvolutionMode) {
      if (!selectedTemplateId) {
        addToast('Selecione um modelo para editar.', 'error');
        return;
      }
      if (!templateText.trim()) {
        addToast('Informe o texto da mensagem.', 'error');
        return;
      }
    } else if (!code.trim() || !templateName.trim()) {
      addToast('Informe o código interno e o nome do template.', 'error');
      return;
    }

    const variablesExample = Object.fromEntries(
      bindingRows
        .filter((r) => r.key.trim() && r.binding.table && r.binding.column)
        .map((r) => [r.key.trim(), r.binding]),
    );

    const payload: Record<string, unknown> = {
      description: description.trim() || undefined,
      templateText: templateText.trim() || undefined,
      variablesExample: Object.keys(variablesExample).length ? variablesExample : undefined,
    };

    if (!isEvolutionMode) {
      Object.assign(payload, {
        code: code.trim(),
        templateName: templateName.trim(),
        languageCode: languageCode.trim() || 'pt_BR',
        hasButtonUrl,
        buttonUrlParamDescription: buttonUrlParamDescription.trim() || undefined,
      });
    } else {
      payload.templateName = templateName.trim() || code.trim();
    }

    if (missingBindings.length > 0) {
      addToast(
        `Mapeie os placeholders {{${missingBindings.join('}}, {{')}} para tabela e campo.`,
        'error',
      );
      return;
    }

    setSaving(true);
    try {
      if (isEditing && selectedTemplateId) {
        await api.patch(`/whatsapp-templates/${selectedTemplateId}`, payload);
        addToast('Modelo de mensagem salvo.', 'success');
      } else {
        await api.post('/whatsapp-templates', {
          code: code.trim(),
          templateName: templateName.trim() || code.trim(),
          ...payload,
          languageCode: languageCode.trim() || 'pt_BR',
          hasButtonUrl,
        });
        addToast('Modelo criado.', 'success');
      }
      await loadTemplates();
      if (!isEvolutionMode) resetForm();
    } catch (e: unknown) {
      console.error('Erro ao salvar template', e);
      addToast(getApiErrorMessage(e, 'Não foi possível salvar o modelo.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const tableOptions = schemaTables.map((t) => ({ value: t.name, label: t.label }));

  return (
    <Card>
      <CardContent className="p-4">
        <View className="mb-3 flex-row items-center gap-3">
          <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
            <Ionicons
              className="text-primary dark:text-white"
              name="chatbubbles-outline"
              size={20}
            />
          </View>
          <View className="flex-1">
            <ThemedText className="font-semibold text-lg text-foreground">
              {isEvolutionMode
                ? 'Mensagens automáticas WhatsApp'
                : 'Modelos de notificação WhatsApp'}
            </ThemedText>
            <ThemedText className="mt-1 text-xs text-foreground/70">
              {isEvolutionMode
                ? 'Edite o texto e mapeie cada {{n}} para uma tabela e campo do sistema.'
                : 'Configure modelos e parâmetros de envio.'}
            </ThemedText>
          </View>
        </View>

        <View className="mt-2 border-b border-border pb-3">
          <View className="mb-2 flex-row items-center justify-between">
            <ThemedText className="text-xs text-foreground/70">
              {isEvolutionMode ? 'Tipos de mensagem' : 'Modelos cadastrados'}
            </ThemedText>
            <Pressable onPress={loadTemplates} disabled={loading || saving}>
              <View className="flex-row items-center gap-1">
                <Ionicons className="text-neutral-500" name="refresh-outline" size={16} />
                <ThemedText className="text-xs text-foreground/70">Recarregar</ThemedText>
              </View>
            </Pressable>
          </View>

          {loading ? (
            <ActivityIndicator size="small" color="#FD5001" />
          ) : displayTemplates.length === 0 ? (
            <ThemedText className="text-sm text-foreground/50">
              Nenhum modelo encontrado.
            </ThemedText>
          ) : (
            <ScrollView
              keyboardShouldPersistTaps="handled"
              className="max-h-48"
              nestedScrollEnabled
            >
              {displayTemplates.map((tpl) => (
                <Pressable
                  key={tpl.id}
                  onPress={() => handleSelectTemplate(tpl)}
                  className={`mb-2 rounded-lg border px-3 py-2 ${
                    selectedTemplateId === tpl.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <ThemedText className="font-medium text-sm text-foreground">
                    {isEvolutionMode && isFixedCode(tpl.code)
                      ? FIXED_TEMPLATE_LABELS[tpl.code as (typeof FIXED_TEMPLATE_CODES)[number]]
                      : tpl.code}
                  </ThemedText>
                  <ThemedText className="text-xs text-foreground/60">
                    {tpl.description || tpl.code}
                  </ThemedText>
                </Pressable>
              ))}
            </ScrollView>
          )}
        </View>

        {isEditing && (
          <View className="mt-3 gap-3">
            {isEvolutionMode && (
              <View>
                <ThemedText className="mb-1 text-xs text-foreground/60">
                  Descrição interna
                </ThemedText>
                <TextInput
                  value={description}
                  onChangeText={setDescription}
                  multiline
                  className="rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground"
                  editable={!saving}
                />
              </View>
            )}

            <View>
              <ThemedText className="mb-1 text-xs text-foreground/60">Texto da mensagem</ThemedText>
              <TextInput
                value={templateText}
                onChangeText={setTemplateText}
                multiline
                numberOfLines={10}
                textAlignVertical="top"
                placeholder="Use {{1}}, {{2}}…"
                placeholderTextColor="#a1a1aa"
                className="min-h-[160px] rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground"
                editable={!saving}
              />
            </View>

            {isEvolutionMode && (
              <View>
                <ThemedText className="mb-2 font-medium text-xs text-foreground/70">
                  Mapeamento placeholder → banco de dados
                </ThemedText>
                {schemaLoading ? (
                  <ActivityIndicator size="small" color="#FD5001" />
                ) : bindingRows.length === 0 ? (
                  <ThemedText className="text-xs text-foreground/50">
                    Adicione placeholders no texto (ex.: {'{{1}}'}) para mapear campos.
                  </ThemedText>
                ) : (
                  bindingRows.map((row, index) => {
                    const tableMeta = schemaTables.find((t) => t.name === row.binding.table);
                    const columnOptions =
                      tableMeta?.columns.map((c) => ({
                        value: c.name,
                        label: c.label,
                      })) ?? [];

                    return (
                      <View
                        key={row.key}
                        className="mb-3 rounded-lg border border-border bg-muted/20 p-3"
                      >
                        <ThemedText className="mb-2 font-medium text-sm text-primary">
                          {`{{${row.key}}}`}
                        </ThemedText>
                        <View className="flex-row gap-2">
                          <InlineSelect
                            label="Tabela"
                            value={row.binding.table}
                            options={tableOptions}
                            disabled={saving}
                            onChange={(v) => updateBindingRow(index, { table: v })}
                          />
                        </View>
                        {tableMeta?.requiresVia && (
                          <View className="mt-2">
                            <InlineSelect
                              label="Contexto"
                              value={row.binding.via ?? ''}
                              options={tableMeta.viaOptions ?? []}
                              disabled={saving}
                              onChange={(v) => updateBindingRow(index, { via: v })}
                            />
                          </View>
                        )}
                        <View className="mt-2">
                          <InlineSelect
                            label="Campo"
                            value={row.binding.column}
                            options={columnOptions}
                            disabled={saving || !row.binding.table}
                            onChange={(v) => updateBindingRow(index, { column: v })}
                          />
                        </View>
                      </View>
                    );
                  })
                )}
              </View>
            )}

            {previewText ? (
              <View className="rounded-lg border border-dashed border-border bg-muted/30 p-3">
                <ThemedText className="mb-1 text-xs text-foreground/60">Prévia</ThemedText>
                <ThemedText className="whitespace-pre-wrap text-sm text-foreground">
                  {previewText}
                </ThemedText>
              </View>
            ) : null}

            <View className="flex-row justify-end">
              <Button onPress={handleSave} disabled={saving}>
                {saving ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <ThemedText className="font-medium text-sm text-white">
                    Salvar mensagem
                  </ThemedText>
                )}
              </Button>
            </View>
          </View>
        )}
      </CardContent>
    </Card>
  );
}
